import { MousePointer2, Hand, MessageCircle, Search } from 'lucide-react'
import clsx from 'clsx'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import { getCurrentDirection } from '@/i18n/config'
import { useTranslation } from '@/i18n'
import { ModeType } from '@/types/graph'

interface GraphToolbarV2Props {
  currentMode: string
  isReadOnly: boolean
  onToolChange: (type: string) => void
  isCommentActive?: boolean
  /**
   * Opens find-in-drawing. Only passed when this editor owns the
   * floating search — in the full V2 editor the sub-navbar carries the input,
   * and a second entry point here would open a panel the user cannot see.
   */
  onSearchClick?: () => void
  isSearchActive?: boolean
}

const ICON_SIZE = 22
const BTN_BASE = 'flex items-center justify-center rounded-lg size-9 transition-colors'

interface ToolItem {
  icon: React.ReactNode
  type: string
  labelKey: string
}

const V2_TOOLS: ToolItem[] = [
  { icon: <MousePointer2 size={ICON_SIZE} />, type: 'select', labelKey: 'board:toolbar.tools.select' },
  { icon: <Hand size={ICON_SIZE} />, type: 'hand', labelKey: 'board:toolbar.tools.move' },
  { icon: <MessageCircle size={ICON_SIZE} />, type: 'comment', labelKey: 'board:toolbar.tools.comment' },
]

export default function GraphToolbarV2({
  currentMode,
  isReadOnly,
  onToolChange,
  isCommentActive,
  onSearchClick,
  isSearchActive,
}: GraphToolbarV2Props) {
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'
  const tooltipSide = isRTL ? 'left' : 'right'

  const tools = isReadOnly ? V2_TOOLS.filter(t => t.type === 'hand') : V2_TOOLS

  const isActive = (type: string) => {
    if (type === 'comment') return isCommentActive || false
    if (currentMode === ModeType.Select && type === 'select') return true
    if (currentMode === ModeType.Drag && type === 'hand') return true
    return false
  }

  // UI-30 (Putra, 2026-08-28): "pnid viewer undo and redo not consistent with
  // board". They used to sit at the top of this rail at 22px with their own
  // hover/disabled styling and a right-side tooltip, while the board renders
  // them inside the bottom zoom pill at 16px from the shared <ZoomControls>.
  // They now come from that same component on both surfaces, so this rail is
  // tools only — which is also what makes them consistent rather than merely
  // similar: there is one implementation left, not two.

  return (
    <div
      className={clsx(
        'absolute top-1/2 -translate-y-1/2 z-20',
        isRTL ? 'right-4' : 'left-4'
      )}
    >
      <div className="bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-2 flex flex-col gap-2 items-center">
        {onSearchClick && (
          <>
            <IntuiTooltip content={String(t('board:toolbar.tools.search'))} side={tooltipSide}>
              <button
                type="button"
                aria-label={String(t('board:toolbar.tools.search'))}
                onClick={onSearchClick}
                className={clsx(
                  BTN_BASE,
                  isSearchActive
                    ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                    : 'text-muted-foreground hover:bg-[#f0f1f4] dark:hover:bg-dark-muted'
                )}
              >
                <Search size={ICON_SIZE} />
              </button>
            </IntuiTooltip>
            <div className="w-6 h-px bg-[#e5e5e5] dark:bg-dark-border" />
          </>
        )}
        {tools.map((tool) => (
          <IntuiTooltip key={tool.type} content={t(tool.labelKey)} side={tooltipSide}>
            <button
              type="button"
              onClick={() => onToolChange(tool.type)}
              className={clsx(
                BTN_BASE,
                isActive(tool.type)
                  ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                  : 'text-muted-foreground hover:bg-[#f0f1f4] dark:hover:bg-dark-muted'
              )}
            >
              {tool.icon}
            </button>
          </IntuiTooltip>
        ))}
      </div>
    </div>
  )
}
